import {
  parseUrn,
  transformUrnToWitboostId,
} from '@agilelab/plugin-wb-builder-common';
import {
  Column,
  createNameWithVersionMap,
  encodeBase64PageCursor,
  GenericEntityType,
  GenericProviderType,
  getLabelAndValue,
  KindEnum,
  NotFoundException,
  Pagination,
  parseDisplayField,
  Provider,
  selectTemplateEntity,
  TemplateEntity,
  TextFieldValue,
  WbSafeNunjucksApi,
} from '@agilelab/plugin-wb-platform';
import { Environment } from '@agilelab/plugin-wb-platform-common';
import { READS_FROM } from '@agilelab/plugin-wb-practice-shaper-common';
import {
  FilterResolversOverride,
  SearchFiltersContext,
  useSearchFilters,
  WitboostSearchApi,
  witboostSearchApiRef,
} from '@agilelab/plugin-wb-search';
import {
  AtomicFilter,
  CollatorType,
  ComplexFilter,
  FilterOperator,
  JsonObjectSearchFilterVisitor,
  PROVISIONING_CONSUMABLE_DOMAIN,
  PROVISIONING_CONSUMABLE_ENVIRONMENT,
  PROVISIONING_CONSUMABLE_KIND,
  PROVISIONING_CONSUMABLE_PARENT,
  PROVISIONING_CONSUMABLE_URN,
  QueryOperator,
  SearchFilterConfigType,
} from '@agilelab/plugin-wb-search-common';
import { parseEntityRef } from '@mainstage/catalog-model';
import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import { catalogApiRef } from '@mainstage/plugin-catalog-react';
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useAsync from 'react-use/lib/useAsync';
import { contextProviderFactory } from '../entityProvider.factory';
import { useColumns } from '../hooks';
import {
  ConsumableFiltersSection,
  ConsumablesFilters,
} from './ConsumablesFilters';
import { gql, useLazyQuery } from '@apollo/client';

export type ConsumableTypeEntity = {} & GenericEntityType;

interface ConsumableContextType
  extends GenericProviderType<ConsumableTypeEntity[], ConsumablesFilters> {
  availableEnvs?: Environment[];
  filtersContext: SearchFiltersContext;
}

const [useConsumableContext, ConsumableContextProvider] =
  contextProviderFactory<ConsumableContextType>();

export default useConsumableContext;
export interface ConsumableProviderProps {
  templateKind: TemplateEntity | undefined;
  children: ReactNode;
  parentTemplateRef?: string;
  environments: Environment[];
  priorityEnv: Environment;
}

const defaultColumns = new Map<string, string>([
  ['name', '{{ name }}'],
  ['description', '{{ description }}'],
  ['domain', '{{_computedInfo.domain.name}}'],
]);

const toConsumableTypeEntity = (
  consumable: Record<string, any>,
): ConsumableTypeEntity => {
  return {
    ...consumable,
    __metadata: { kind: KindEnum.consumable, name: consumable.name },
  };
};

export const getMarketplaceSystemsByURNsQuery = gql`
  query getMarketplaceSystemsByURNs($urns: [String!], $env: String) {
    data: marketplace_instances(
      where: {
        environment: { name: { _eq: $env } }
        external_id: { _in: $urns }
      }
    ) {
      id: external_id
      name
      display_name
      version
      value: external_id
    }
  }
`;

export const getMarketplaceDomainsByURNsQuery = gql`
  query getMarketplaceSystemsByURNs($urns: [String!]) {
    data: marketplace_instances(where: { external_id: { _in: $urns } }) {
      id: external_id
      name
      display_name
      version
      value: external_id
    }
  }
`;

export const ConsumablesProvider: FC<ConsumableProviderProps> = props => {
  const {
    children,
    templateKind,
    parentTemplateRef,
    environments,
    priorityEnv,
  } = props;

  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [prevCursor, setPrevCursor] = useState<string | undefined>(undefined);
  const [pagination, setPagination] = useState<Pagination>({
    limit: 10,
    currentPage: 0,
    cursor: encodeBase64PageCursor(0),
  });
  const searchApi = useApi(witboostSearchApiRef);
  const [count, setCount] = useState<number>(-1);
  const [filters, setFilters] = useState<ConsumablesFilters>({});
  const [readableResourceTypes, setReadableResourceTypes] = useState<
    string[] | undefined
  >([]);
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const preFilters = templateKind?.filter;

  useEffect(() => {
    if (priorityEnv) {
      setFilters({
        environment: priorityEnv,
      });
    }
  }, [priorityEnv]);

  const getReadableResourceTypes = useCallback(
    async (componentTypeRef: string) => {
      const { token } = await identityApi.getCredentials();
      const parsedEntityRef = parseEntityRef(componentTypeRef);
      if (parsedEntityRef.kind.toLowerCase() !== 'componenttype') {
        return [];
      }
      const componentType = await catalogApi.getEntityByRef(parsedEntityRef, {
        token,
      });
      const readsFromRelations =
        componentType?.relations?.filter(rel => rel.type === READS_FROM) ?? [];
      const readableTypes = readsFromRelations.map(rel => rel.targetRef);
      return (
        await catalogApi.getEntitiesByRefs(
          { entityRefs: readableTypes },
          { token },
        )
      ).items
        .filter(item => !!item)
        .map(item => item?.spec?.resourceTypeId) as string[];
    },
    [catalogApi, identityApi],
  );

  const fetchResourceTypesCompatibleWithCurrentTemplate =
    useCallback(async () => {
      const { token } = await identityApi.getCredentials();
      if (!parentTemplateRef) return [];

      const parent = await catalogApi.getEntityByRef(parentTemplateRef, {
        token,
      });

      if (!parent || !parent.spec?.generates) return [];

      return getReadableResourceTypes(parent.spec.generates as string);
    }, [catalogApi, getReadableResourceTypes, identityApi, parentTemplateRef]);

  const fetchResourceTypesFromCanBeReadFrom = useCallback(
    async (readableEntityRef: string) => {
      return getReadableResourceTypes(readableEntityRef);
    },
    [getReadableResourceTypes],
  );

  useEffect(() => {
    const getProvisioningComponents = async () => {
      let resourceTypes: string[] | undefined = undefined;

      const kind = preFilters?.practiceShaper?.kind;

      if (kind === 'canBeReadFromCurrentInstance') {
        resourceTypes =
          (await fetchResourceTypesCompatibleWithCurrentTemplate()) ?? [];
      }

      if (
        kind === 'canBeReadFrom' &&
        preFilters?.practiceShaper.canBeReadFrom
      ) {
        resourceTypes =
          (await fetchResourceTypesFromCanBeReadFrom(
            preFilters?.practiceShaper.canBeReadFrom,
          )) ?? [];
      }

      setReadableResourceTypes(resourceTypes);
    };

    getProvisioningComponents();
  }, [
    fetchResourceTypesCompatibleWithCurrentTemplate,
    fetchResourceTypesFromCanBeReadFrom,
    preFilters?.practiceShaper.canBeReadFrom,
    preFilters?.practiceShaper?.kind,
  ]);

  const currentFilters = useMemo(() => {
    const adaptedFilter = [];
    if (filters.domain)
      adaptedFilter.push(
        new AtomicFilter(
          PROVISIONING_CONSUMABLE_DOMAIN.field,
          FilterOperator.IN,
          filters.domain.map(d => d.value),
        ),
      );

    if (filters.environment)
      adaptedFilter.push(
        new AtomicFilter(
          PROVISIONING_CONSUMABLE_ENVIRONMENT.field,
          FilterOperator.IN,
          [filters.environment.name],
        ),
      );

    if (filters.system)
      adaptedFilter.push(
        new AtomicFilter(
          PROVISIONING_CONSUMABLE_PARENT.field,
          FilterOperator.IN,
          filters.system.map(s => s.value),
        ),
      );

    if (readableResourceTypes) {
      adaptedFilter.push(
        new AtomicFilter(
          PROVISIONING_CONSUMABLE_KIND.field,
          FilterOperator.IN,
          readableResourceTypes,
        ),
      );
    }

    return new ComplexFilter(QueryOperator.AND, adaptedFilter).accept(
      new JsonObjectSearchFilterVisitor(),
    );
  }, [filters, readableResourceTypes]);

  const [resolveParents] = useLazyQuery(getMarketplaceSystemsByURNsQuery);
  const [resolveDomains] = useLazyQuery(getMarketplaceDomainsByURNsQuery);

  const filterResolversOverride = useMemo<FilterResolversOverride>(
    () => ({
      [SearchFilterConfigType.CHOICE]: async values => {
        let parentData: { name: string; value: string }[] = [];
        let domainData: { name: string; value: string }[] = [];

        if (values?.[PROVISIONING_CONSUMABLE_PARENT.label]) {
          const urns = values[PROVISIONING_CONSUMABLE_PARENT.label].map(
            el => el.value,
          );

          const { data } = await resolveParents({
            variables: {
              urns,
              env: filters?.environment?.name ?? priorityEnv.name,
            },
          });

          const nameWithVersionMap = createNameWithVersionMap(
            data.data,
            'display_name',
          );

          parentData = urns.map(urn => ({
            name: nameWithVersionMap[urn]?.nameWithVersion ?? urn,
            value: urn,
          }));
        }

        if (values?.[PROVISIONING_CONSUMABLE_DOMAIN.label]) {
          const domainUrns = values[PROVISIONING_CONSUMABLE_DOMAIN.label].map(
            el => el.value,
          );

          const res = await resolveDomains({
            variables: { urns: domainUrns },
          });

          const domainMap = new Map<string, string>(
            res.data.data.map((d: any) => [d.value, d.display_name]),
          );

          domainData = domainUrns.map(urn => ({
            name: domainMap.get(urn) ?? urn,
            value: urn,
          }));
        }

        return filter => {
          if (!values?.[filter.label]) {
            return [];
          }

          if (filter.label === PROVISIONING_CONSUMABLE_PARENT.label) {
            return parentData;
          }

          if (filter.label === PROVISIONING_CONSUMABLE_DOMAIN.label) {
            return domainData;
          }

          return values[filter.label];
        };
      },
    }),
    [
      resolveParents,
      filters?.environment?.name,
      priorityEnv.name,
      resolveDomains,
    ],
  );

  const filtersContext = useSearchFilters(
    CollatorType.PROVISIONING_CONSUMABLES,
    filterResolversOverride,
    currentFilters,
    filters.search,
  );

  const consumableState = useAsync(async () => {
    if (readableResourceTypes?.length === 0) {
      return undefined;
    }

    const response = await searchApi.query({
      term: filters.search ?? '',
      filters: currentFilters,
      pageCursor: pagination.cursor,
      pageLimit: pagination.limit,
      types: [CollatorType.PROVISIONING_CONSUMABLES],
      orderBy: { field: 'rank', order: 'desc' },
    });

    if (response.numberOfResults) setCount(response.numberOfResults);

    setNextCursor(response.nextPageCursor);
    setPrevCursor(response.previousPageCursor);

    return response.results.map((r: any) => toConsumableTypeEntity(r.document));
  }, [filters, pagination.cursor, pagination.limit, readableResourceTypes]);

  const columns: Column[] = useColumns(defaultColumns, templateKind);

  return (
    <ConsumableContextProvider
      value={{
        entitiesState: consumableState,
        columns,
        filters,
        changeFilters: <K extends keyof ConsumablesFilters>(
          key: K,
          filterValue: ConsumablesFilters[K],
        ) => {
          setPagination(p => ({ ...p, currentPage: 0, cursor: undefined }));
          setFilters(f => ({ ...f, [key]: filterValue }));
        },
        resetFilters: () => {
          setFilters({
            environment: priorityEnv,
          });
        },
        templateKind,
        pagination,
        setPagination,
        count,
        nextCursor,
        prevCursor,
        availableEnvs: environments,
        filtersContext,
      }}
    >
      {children}
    </ConsumableContextProvider>
  );
};

export class ConsumablesHandlerProvider
  implements Provider<ConsumableTypeEntity[], ConsumablesFilters>
{
  private readonly searchApi: WitboostSearchApi;

  private readonly environments: Environment[];
  private readonly priorityEnv: Environment;
  private readonly templateEntity: TemplateEntity | undefined;
  private readonly nunjucksApi: WbSafeNunjucksApi;

  constructor(
    nunjucksApi: WbSafeNunjucksApi,

    searchApi: WitboostSearchApi,
    availableKinds: TemplateEntity[],
    environments: Environment[],
    priorityEnv: Environment,
  ) {
    this.nunjucksApi = nunjucksApi;
    this.searchApi = searchApi;
    this.environments = environments;
    this.priorityEnv = priorityEnv;
    this.templateEntity = selectTemplateEntity(
      availableKinds,
      KindEnum.consumable,
    );
  }

  useContext = useConsumableContext;

  renderContextProvider(
    templateKind: TemplateEntity | undefined,
    children: ReactNode,
    parentTemplateRef?: string,
  ) {
    return (
      <ConsumablesProvider
        templateKind={templateKind}
        parentTemplateRef={parentTemplateRef}
        environments={this.environments}
        priorityEnv={this.priorityEnv}
      >
        {children}
      </ConsumablesProvider>
    );
  }

  filtersSection = ConsumableFiltersSection;

  adapt(entity: GenericEntityType) {
    const consumableInterfaceRef = transformUrnToWitboostId(entity.id);

    if (!consumableInterfaceRef) {
      throw new Error(
        `Invalid URN. Check your entity id: ${JSON.stringify(entity)}`,
      );
    }
    const consumableInterfaceCompoundRef = parseEntityRef(
      consumableInterfaceRef,
    );

    const parsedDisplayField = parseDisplayField(
      this.nunjucksApi,
      entity,
      this.templateEntity?.displayField,
    );

    return {
      name: consumableInterfaceCompoundRef.name,
      kind: KindEnum.consumable,
      label: parsedDisplayField ?? entity.name,
      urn: entity.id,
      // for consumableInterface only urn or fullObj could be returned
      // for fault tolerance urn will be returned if ref is selected on the template
      ref: entity.id,
      ...entity,
    };
  }

  async resolve(urn: string): Promise<TextFieldValue | undefined> {
    const availableEnvs = this.environments;
    try {
      const parsedUrn = parseUrn(urn);
      if (parsedUrn.kind === 'cmp') {
        const jsonSearchFilter = new ComplexFilter(QueryOperator.AND, [
          new AtomicFilter(
            PROVISIONING_CONSUMABLE_URN.field,
            FilterOperator.EQ,
            urn,
          ),
        ]).accept(new JsonObjectSearchFilterVisitor());

        try {
          const data = await this.searchApi.query({
            term: '',
            filters: jsonSearchFilter,
            types: [CollatorType.PROVISIONING_CONSUMABLES],
            orderBy: { field: 'rank', order: 'desc' },
          });

          let consumable: any = undefined;
          for (const env of availableEnvs) {
            const possibleConsumable = data.results?.find(res => {
              return (res.document as any).environment === env.name;
            });

            if (possibleConsumable) {
              consumable = possibleConsumable;
              break;
            }
          }

          if (!consumable) {
            throw new NotFoundException(
              `Cannot find a Consumable entity with ref: ${urn}`,
            );
          }

          return getLabelAndValue(
            consumable.document,
            this.adapt(consumable.document),
            this.templateEntity,
          );
        } catch (error) {
          throw new Error(`Error while resolving entity with ref: ${urn}`);
        }
      }
    } catch (e) {
      return undefined;
    }
    return undefined;
  }
}
